/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { IonicSelectableComponent } from '@ionic-selectable/angular';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '@pages/login/components/login.service';
import { PROFILEIDS } from '@shared/models/constants/profileIds';
import { IRouteRegisterParams } from '@shared/models/interfaces/IRouteRegisterParams.interface';
import { Register } from '@shared/models/interfaces/register.interface';
import { UtmModel } from '@shared/models/interfaces/utmModel.interface';
import { StateCityService } from '@shared/providers/state-city.service';
import { UtilsService } from '@shared/providers/utils.service';
import { ZipcodeSearchService } from '@shared/providers/zipcode-search.service';
import { CNPJValidator } from '@shared/validators/cnpj.validators';
import { CPFValidator } from '@shared/validators/cpf.validator';
import { PhoneValidator } from '@shared/validators/phone.validator';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-register-form',
  templateUrl: './register-form.component.html',
  styleUrls: ['./register-form.component.scss'],
})
export class RegisterFormComponent implements OnInit {
  public registerForm: FormGroup;
  public showPass = false;
  public selectedRadioGroup = '1';
  @Output() submitForm = new EventEmitter<{ utmJson: UtmModel; user: Register }>();
  public statesList = [];
  public citiesList = [];
  public allCities = [];
  public citiesPerPage = 20;
  public citiesPage = 1;
  public isIos = true;
  public trialDays: { value: string } = { value: null };
  public subtitleText =
    "(!isIos ? 'NEW_USER.YOU_WILL_BE_ABLE_TO_USE_THE_APP_FOR_FREE_FOR_A_PEDIOD' : '') | translate trialDays 'NEW_USERDAYS' | translate";

  private zipCodeSubscription = new Subscription();
  private cnpjSubscription = new Subscription();

  public termsOfUseLink = environment.termsOfUseLink;
  public usagePolice = environment.usagePolice;
  public planId = environment.newUserPlanId;
  public params: IRouteRegisterParams;
  public utmJson: UtmModel;

  constructor(
    private formBuilder: FormBuilder,
    private platform: Platform,
    private zipcodeSearchService: ZipcodeSearchService,
    private stateCityService: StateCityService,
    private utilsService: UtilsService,
    private router: ActivatedRoute,
    private translate: TranslateService,
    private loginService: LoginService
  ) {
    this.router.queryParams.subscribe(params => {
      this.params = params;
      if (this.params.userType && this.params.userType.toLowerCase() === 'pj') {
        this.selectedRadioGroup = '2';
      }

      if (this.params.nome) {
        this.userName.setValue(this.params.nome);
      }

      if (this.params.email) {
        this.userEmail.setValue(this.params.email);
      }

      if (this.params.telefone) {
        this.userPhone.setValue(this.params.telefone);
      }

      if (this.params.utm_medium) {
        this.utmJson.utm_medium = this.params.utm_medium;
        this.utmJson.utm_source = this.params.utm_source;
        this.utmJson.utm_campaign = this.params.utm_campaign;
      }
    });
  }

  // #region user
  get user() {
    return this.registerForm.get('user');
  }

  get userName() {
    return this.registerForm.get('user').get('userName');
  }

  get cpf() {
    return this.registerForm.get('user').get('cpf');
  }

  get cnpj() {
    return this.registerForm.get('user').get('cnpj');
  }

  get userResponsibleCreci() {
    return this.registerForm.get('user').get('userResponsibleCreci');
  }

  get userPhone() {
    return this.registerForm.get('user').get('userPhone');
  }

  get userEmail() {
    return this.registerForm.get('user').get('userEmail');
  }

  get userCode() {
    return this.registerForm.get('user').get('userCode');
  }

  get password() {
    return this.registerForm.get('user').get('password');
  }
  // #endregion

  // #region address
  get zipCode() {
    return this.registerForm.get('masterAdmins').get('zipCode');
  }
  get street() {
    return this.registerForm.get('masterAdmins').get('street');
  }
  get streetNumber() {
    return this.registerForm.get('masterAdmins').get('streetNumber');
  }
  get complement() {
    return this.registerForm.get('masterAdmins').get('complement');
  }
  get neighborhood() {
    return this.registerForm.get('masterAdmins').get('neighborhood');
  }
  get state() {
    return this.registerForm.get('masterAdmins').get('state');
  }
  get city() {
    return this.registerForm.get('masterAdmins').get('city');
  }
  // #endregion

  // #region responsible
  get masterAdminResponsibles() {
    return this.registerForm.get('masterAdminResponsibles');
  }
  get responsibleName() {
    return this.registerForm.get('masterAdminResponsibles').get('masterAdminResponsibleName');
  }

  get responsibleDocument() {
    return this.registerForm.get('masterAdminResponsibles').get('masterAdminResponsibleCpf');
  }

  get responsiblePhone() {
    return this.registerForm.get('masterAdminResponsibles').get('masterAdminResponsiblePhone');
  }

  get responsibleEmail() {
    return this.registerForm.get('masterAdminResponsibles').get('masterAdminResponsibleEmail');
  }
  // #endregion

  // #region terms
  get checkbox() {
    return this.registerForm.get('checkbox');
  }
  // #endregion

  // #region couponCode
  get couponCode() {
    return this.registerForm.get('couponCode');
  }
  // #endregion

  radioGroupChange(event) {
    if (event.detail.value !== this.selectedRadioGroup) {
      this.selectedRadioGroup = event.detail.value;
      if (this.selectedRadioGroup === '1') {
        this.cnpj.clearValidators();
        this.cpf.setValidators(Validators.compose([Validators.required, CPFValidator.isValid]));
        this.userResponsibleCreci.clearValidators();
        this.responsibleName.clearValidators();
        this.responsibleDocument.clearValidators();
        this.responsibleEmail.clearValidators();
        this.responsiblePhone.clearValidators();
      } else {
        this.userResponsibleCreci.setValidators(Validators.compose([Validators.maxLength(20)]));
        this.cpf.clearValidators();
        this.cnpj.setValidators(Validators.compose([Validators.required, CNPJValidator.isValid]));

        this.responsibleName.setValidators(
          Validators.compose([Validators.required, Validators.maxLength(90)])
        );

        this.responsibleEmail.setValidators(Validators.compose([Validators.email, Validators.maxLength(90)]));
        this.responsiblePhone.setValidators(Validators.compose([PhoneValidator.isValid]));
      }
      this.cpf.updateValueAndValidity();
      this.cnpj.updateValueAndValidity();
      this.userResponsibleCreci.updateValueAndValidity();
      this.responsibleName.updateValueAndValidity();
      this.responsibleDocument.updateValueAndValidity();
      this.responsibleEmail.updateValueAndValidity();
      this.responsiblePhone.updateValueAndValidity();
    }
  }

  async ngOnInit() {
    this.registerForm = this.initializeForm();
    this.statesList = this.stateCityService.getStates();
    this.isIos = this.platform.is('ios');
    await this.setSubtitleText();

    // Listen to changes in zip code to update city and state
    this.zipCodeSubscription = this.zipCode.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(800))
      .subscribe(async zipCode => {
        if (zipCode !== '' && zipCode?.length >= 9) {
          try {
            await this.utilsService.presentLoading();
            const address = await this.zipcodeSearchService.searchStateAndCityByZipCode(zipCode);
            const index = this.statesList.findIndex(a => a.sigla === address.uf);
            if (index !== -1) {
              this.registerForm.patchValue({
                masterAdmins: {
                  street: address.logradouro,
                  neighborhood: address.bairro,
                  complement: address.complemento,
                },
              });
              this.state.setValue(this.statesList[index]);
              this.stateSelected({ value: this.statesList[index] });
              const i = this.allCities.findIndex(
                a => a.normalized === this.utilsService.normalizeString(address.localidade)
              );
              this.city.setValue(this.allCities[i]);
            }
          } catch (e) {
            console.error(e);
          } finally {
            if (zipCode !== '') {
              this.utilsService.dismissLoading();
            }
          }
        }
      });

    // Listen to changes in CNPJ and zip code to update city and state
    this.cnpjSubscription = this.cnpj.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(800))
      .subscribe(async cnpj => {
        if (cnpj !== '' && cnpj.length > 13) {
          try {
            await this.utilsService.presentLoading();
            let data = await this.zipcodeSearchService.searchCNPJ(cnpj);

            this.userName.setValue(data['nome']);
            this.userEmail.setValue(data['email']);
            this.userPhone.setValue(data['telefone']);
            this.zipCode.setValue(data['cep']);
          } catch (e) {
            console.error(e);
          } finally {
            if (cnpj !== '') {
              this.utilsService.dismissLoading();
            }
          }
        }
      });
  }
  initializeForm() {
    const user = this.formBuilder.group({
      userName: [this.params.nome || '', Validators.compose([Validators.required, Validators.maxLength(90)])],
      cpf: ['', Validators.compose([Validators.required, CPFValidator.isValid])],
      cnpj: [''],
      userPhone: [
        this.params.telefone || '',
        Validators.compose([Validators.required, PhoneValidator.isValid]),
      ],
      userEmail: [
        this.params.email || '',
        Validators.compose([Validators.required, Validators.email, Validators.maxLength(90)]),
      ],
      userCode: [0, Validators.compose([Validators.maxLength(5)])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(8)])],
      userResponsibleCreci: [''],
    });

    const masterAdmins = this.formBuilder.group({
      zipCode: [this.params.zipcode || '', Validators.compose([Validators.maxLength(11)])],
      street: ['', Validators.compose([Validators.maxLength(60)])],
      streetNumber: ['', Validators.compose([Validators.maxLength(10)])],
      complement: ['', Validators.maxLength(60)],
      neighborhood: ['', Validators.compose([Validators.maxLength(50)])],
      state: [''],
      city: [{ value: '', disabled: true }],
    });

    const masterAdminResponsibles = this.formBuilder.group({
      masterAdminResponsibleName: [''],
      masterAdminResponsibleCpf: [''],
      masterAdminResponsibleEmail: [''],
      masterAdminResponsiblePhone: [''],
    });

    return this.formBuilder.group({
      user,
      masterAdmins,
      masterAdminResponsibles,
      checkbox: [null, Validators.compose([Validators.required])],
      couponCode: [''],
    });
  }

  async setSubtitleText() {
    let trial = await this.loginService.getTrial();
    this.trialDays.value = trial.trialDays;
  }

  stateSelected(state: any) {
    this.handleStateChange(state.value.sigla);
    this.city.enable();
    this.city.setValue('');
    this.citiesPage = 1;
  }

  handleStateChange(selectedStateAcronym: string) {
    const selectedState = this.stateCityService.getStatesByAcronym(selectedStateAcronym);
    if (selectedState) {
      this.allCities = this.stateCityService.getCitiesFromState(selectedState.id);
      this.citiesList = this.allCities.slice(0, this.citiesPerPage);
    }
  }

  getMoreCities(event: { component: IonicSelectableComponent; text: string }) {
    if (this.citiesPerPage * this.citiesPage > this.allCities.length) {
      event.component.disableInfiniteScroll();
      return;
    }

    this.citiesList = this.citiesList.concat(
      this.allCities.slice(this.citiesPerPage * this.citiesPage++, this.citiesPerPage * this.citiesPage)
    );

    event.component.endInfiniteScroll();
  }

  searchCities(event: { component: IonicSelectableComponent; text: string }) {
    if (event.text) {
      event.component.startSearch();

      const cities = this.allCities.filter(city => {
        return city.normalized.includes(this.utilsService.normalizeString(event.text));
      });

      event.component.items = cities;
    } else {
      event.component.items = this.citiesList;
    }
    event.component.endSearch();
  }

  searchState(event: { component: IonicSelectableComponent; text: string }) {
    if (event.text) {
      event.component.startSearch();

      const states = this.statesList.filter(state => {
        return state.normalized.includes(this.utilsService.normalizeString(event.text));
      });

      event.component.items = states;
    } else {
      event.component.items = this.statesList;
    }
    event.component.endSearch();
  }

  async requestNewUser(event) {
    event.preventDefault();
    if (!this.checkbox.value) {
      this.checkbox.setValue(null);
    }
    if (!this.registerForm.valid) {
      this.utilsService.validateAllFields(this.registerForm);
      this.utilsService.showToast('', `${this.translate.instant('NEW_USER.CHECK_THE_DATA')}`);
      return;
    }
    const requestValue = this.registerForm.getRawValue();
    if (this.selectedRadioGroup === '1') {
      delete requestValue.masterAdminResponsibles;
    }

    const planFormdata = {
      planId: this.planId,
    };
    requestValue.user.userPhone = this.utilsService.unmask(requestValue.user.userPhone);
    if (this.selectedRadioGroup === '2') {
      requestValue.user.userDocument = this.utilsService.unmask(requestValue.user.cnpj);
      requestValue.masterAdminResponsibles.masterAdminResponsibleCpf = this.utilsService.unmask(
        requestValue.masterAdminResponsibles.masterAdminResponsibleCpf
      );
      requestValue.masterAdminResponsibles.masterAdminResponsiblePhone = this.utilsService.unmask(
        requestValue.masterAdminResponsibles.masterAdminResponsiblePhone
      );
    } else {
      requestValue.user.userDocument = this.utilsService.unmask(requestValue.user.cpf);
    }
    requestValue.masterAdmins = {
      ...this.utilsService.parseAddressToMasterAdminFormat(requestValue.masterAdmins),
      ...planFormdata,
    };
    const data: Register = {
      ...requestValue,
      planId: this.planId,
      documentTypeId: this.selectedRadioGroup === '2' ? 3 : 2,
      profileId: PROFILEIDS.adminMaster,
      conventionalLogin: {
        conventionalLoginEmail: requestValue.user.userEmail,
        conventionalLoginPassword: requestValue.user.password,
      },
      personTypeId: this.selectedRadioGroup === '2' ? 2 : 1,
      couponCode: this.couponCode.value,
      recomendationUserId: this.params.recomendation ? Number(this.params.recomendation) : null,
    };
    const utmJson = {
      ...this.utmJson,
      email: this.userEmail.value,
      nome: this.userName.value,
      telefone: this.userPhone.value,
    };
    this.submitForm.emit({ user: data, utmJson });
  }

  ngOnDestroy() {
    this.zipCodeSubscription.unsubscribe();
  }
}
